<template>
	<div class="poster-view">
		<h1>Poster Generator</h1>
		<div v-if="!user?.user" class="login-message">
			<p>Je moet ingelogd zijn om de poster generator te gebruiken.</p>
		</div>
		<div v-else class="poster-generator-container">
			<div class="poster-controls">
				<div class="form-group">
					<label for="name-input">Naam:</label>
					<input 
						id="name-input"
						type="text" 
						class="form-control" 
						v-model="personName" 
						placeholder="Bijv. Nora"
					/>
				</div>
				<div class="form-group">
					<label for="photo-input">Foto:</label>
					<div class="file-input-wrapper">
						<input 
							id="photo-input"
							type="file" 
							class="file-input-hidden" 
							accept="image/*"
							@change="handleFileSelect"
							ref="fileInput"
						/>
						<button 
							type="button"
							class="btn btn-file"
							@click="$refs.fileInput.click()"
						>
							{{ selectedPhoto ? 'Foto gewijzigd' : 'Kies foto' }}
						</button>
						<span v-if="selectedPhoto" class="file-name">{{ selectedPhoto.name }}</span>
					</div>
					<small class="form-text text-muted">Selecteer een foto om te uploaden</small>
				</div>
			</div>
			<div class="poster-preview">
				<div ref="posterContainer" class="poster-wrapper">
					<PosterGenerator 
						:name="personName" 
						:photo="selectedPhoto" 
						:userId="user?.user"
					/>
				</div>
			</div>
			<div class="download-buttons">
				<button 
					@click="downloadPNG" 
					class="btn btn-primary"
					:disabled="downloading"
				>
					{{ downloading === 'png' ? 'Downloaden...' : 'Download als PNG' }}
				</button>
				<button 
					@click="downloadSVG" 
					class="btn btn-success"
					:disabled="downloading"
				>
					{{ downloading === 'svg' ? 'Downloaden...' : 'Download als SVG' }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import PosterGenerator from "../components/PosterGenerator.vue";
import { getUser } from "../services/UserService";
import html2canvas from "html2canvas";

export default {
	name: "PosterView",
	components: {
		PosterGenerator
	},
	data() {
		return {
			personName: "Naam",
			selectedPhoto: null,
			user: {},
			downloading: null
		};
	},
	methods: {
		handleFileSelect(event) {
			const file = event.target.files[0];
			if (file) {
				this.selectedPhoto = file;
			}
		},
		async loadUser() {
			try {
				const userData = await getUser();
				this.user = userData || {};
				
				// Set default name to first name (substring before first whitespace)
				if (this.user && this.user.name && this.personName === "Naam") {
					const firstName = this.user.name.split(/\s+/)[0];
					if (firstName) {
						this.personName = firstName;
					}
				}
			} catch (error) {
				console.error('Error loading user:', error);
				this.user = {};
			}
		},
		async downloadPNG() {
			if (!this.$refs.posterContainer) {
				alert('Poster container niet gevonden');
				return;
			}

			this.downloading = 'png';
			try {
				// Wait a bit to ensure everything is rendered
				await this.$nextTick();
				await new Promise(resolve => setTimeout(resolve, 500));

				const canvas = await html2canvas(this.$refs.posterContainer, {
					backgroundColor: '#ffffff',
					scale: 2, // Higher quality
					useCORS: true,
					allowTaint: true,
					logging: false
				});

				// Convert canvas to blob and download
				canvas.toBlob((blob) => {
					const url = URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = url;
					link.download = `poster-${this.personName || 'poster'}-${Date.now()}.png`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					URL.revokeObjectURL(url);
					this.downloading = null;
				}, 'image/png');
			} catch (error) {
				console.error('Error generating PNG:', error);
				alert('Er is een fout opgetreden bij het genereren van de PNG');
				this.downloading = null;
			}
		},
		async downloadSVG() {
			if (!this.$refs.posterContainer) {
				alert('Poster container niet gevonden');
				return;
			}

			this.downloading = 'svg';
			try {
				// Wait a bit to ensure everything is rendered
				await this.$nextTick();
				await new Promise(resolve => setTimeout(resolve, 500));

				// First convert to canvas, then to SVG
				const canvas = await html2canvas(this.$refs.posterContainer, {
					backgroundColor: '#ffffff',
					scale: 2,
					useCORS: true,
					allowTaint: true,
					logging: false
				});

				// Convert canvas to SVG
				const svgString = this.canvasToSVG(canvas);
				const blob = new Blob([svgString], { type: 'image/svg+xml' });
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = `poster-${this.personName || 'poster'}-${Date.now()}.svg`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
				this.downloading = null;
			} catch (error) {
				console.error('Error generating SVG:', error);
				alert('Er is een fout opgetreden bij het genereren van de SVG');
				this.downloading = null;
			}
		},
		canvasToSVG(canvas) {
			const dataURL = canvas.toDataURL('image/png');
			const width = canvas.width;
			const height = canvas.height;
			
			return `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
	<image width="${width}" height="${height}" xlink:href="${dataURL}"/>
</svg>`;
		}
	},
	mounted() {
		this.loadUser();
	}
};
</script>

<style scoped>
.poster-view {
	padding: 20px;
	max-width: 1200px;
	margin: 0 auto;
}

.poster-generator-container {
	display: flex;
	flex-direction: column;
	gap: 30px;
}

.poster-controls {
	background: #f8f9fa;
	padding: 20px;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	gap: 15px;
}

.form-group {
	display: flex;
	flex-direction: column;
	gap: 5px;
}

.form-group label {
	font-weight: bold;
	margin-bottom: 5px;
}

.form-control {
	padding: 8px 12px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 14px;
}

.file-input-wrapper {
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
}

.file-input-hidden {
	position: absolute;
	width: 0;
	height: 0;
	opacity: 0;
	overflow: hidden;
	pointer-events: none;
}

.btn-file {
	padding: 10px 20px;
	font-size: 14px;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	background-color: #6c757d;
	color: white;
	transition: background-color 0.3s;
	font-weight: 500;
}

.btn-file:hover {
	background-color: #5a6268;
}

.file-name {
	font-size: 14px;
	color: #28a745;
	font-weight: 500;
}

.form-text {
	font-size: 12px;
	color: #6c757d;
}

.poster-preview {
	display: flex;
	justify-content: center;
	background: #f8f9fa;
	padding: 20px;
	border-radius: 8px;
}

.poster-wrapper {
	display: inline-block;
}

.download-buttons {
	display: flex;
	gap: 15px;
	justify-content: center;
	margin-top: 20px;
	padding: 20px;
}

.download-buttons .btn {
	padding: 12px 24px;
	font-size: 16px;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	transition: opacity 0.3s;
}

.download-buttons .btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.btn-primary {
	background-color: #007bff;
	color: white;
}

.btn-primary:hover:not(:disabled) {
	background-color: #0056b3;
}

.btn-success {
	background-color: #28a745;
	color: white;
}

.btn-success:hover:not(:disabled) {
	background-color: #218838;
}

.login-message {
	padding: 40px;
	text-align: center;
	background: #fff3cd;
	border: 1px solid #ffc107;
	border-radius: 8px;
	color: #856404;
}

@media (max-width: 768px) {
	.poster-view {
		padding: 10px;
	}
	
	.poster-controls {
		padding: 15px;
	}
}
</style>